import { ROUTES, buildRoute } from '../../config/routes';
import { isInternalAdmin } from '../../util/roles';
import type { AssetsEntry } from './hooks/usePropertyAssets';

/**
 * DV-3345 (spec §4.4, D8–D11): which buttons the photos page's two PMS notices
 * show. On /home, today's in-app navigation. On the Global Orders photos page,
 * an Internal Admin gets real links that open the client's Integrations pages
 * in a new tab (the deep-link bootstrap switches that tab into the client),
 * and every other role gets no way into the account. Contact support is hidden
 * there for everyone: the editors are staff.
 */
export type NoticeAction = { kind: 'navigate' } | { kind: 'link'; href: string } | null;

export interface NoticeInput {
  entry: AssetsEntry;
  /** localStorage.role, read by the caller. */
  role: string | null;
  /** The property's account id. */
  entityUUID: string | null;
  /** The property's account name, for the caption. */
  accountName: string | null;
  /** pmsSource as the page holds it. */
  vendor: string | null;
}

export interface FailedSyncActions {
  checkConnection: NoticeAction;
  contactSupport: NoticeAction;
  /** Footer Close button, shown when no action remains. */
  showClose: boolean;
  /** "Opens in a new tab, signed into {name}." — only when a link is shown. */
  newTabCaption: string | null;
}

export interface PausedNoticeActions {
  goToIntegrations: NoticeAction;
  newTabCaption: string | null;
  body: 'default' | 'ask-admin';
}

/** What every /home caller gets, whatever the role (the Properties list uses it directly). */
export const ACCOUNT_FAILED_SYNC_ACTIONS: FailedSyncActions = {
  checkConnection: { kind: 'navigate' },
  contactSupport: { kind: 'navigate' },
  showClose: false,
  newTabCaption: null
};

export const ACCOUNT_PAUSED_NOTICE_ACTIONS: PausedNoticeActions = {
  goToIntegrations: { kind: 'navigate' },
  newTabCaption: null,
  body: 'default'
};

const inClientAccount = (path: string, entityUUID: string): string =>
  `${path}?${new URLSearchParams({ entityUUID }).toString()}`;

const captionFor = (accountName: string | null): string =>
  `Opens in a new tab, signed into ${accountName ?? "this client's account"}.`;

export function failedSyncActions({ entry, role, entityUUID, accountName, vendor }: NoticeInput): FailedSyncActions {
  if (entry === 'account') return ACCOUNT_FAILED_SYNC_ACTIONS;
  if (isInternalAdmin(role) && entityUUID && vendor) {
    return {
      checkConnection: { kind: 'link', href: inClientAccount(buildRoute.integrationVendor(vendor), entityUUID) },
      contactSupport: null,
      showClose: false,
      newTabCaption: captionFor(accountName)
    };
  }
  return { checkConnection: null, contactSupport: null, showClose: true, newTabCaption: null };
}

/**
 * A sync the server refused (see syncFailure.ts) is not a connection problem,
 * so nothing points at the connection. Close fills in when no action remains.
 */
export function refusedSyncActions(actions: FailedSyncActions): FailedSyncActions {
  return {
    checkConnection: null,
    contactSupport: actions.contactSupport,
    showClose: actions.contactSupport === null,
    newTabCaption: null
  };
}

export function pausedNoticeActions({ entry, role, entityUUID, accountName }: NoticeInput): PausedNoticeActions {
  if (entry === 'account') return ACCOUNT_PAUSED_NOTICE_ACTIONS;
  if (!isInternalAdmin(role)) return { goToIntegrations: null, newTabCaption: null, body: 'ask-admin' };
  if (!entityUUID) return { goToIntegrations: null, newTabCaption: null, body: 'default' };
  return {
    goToIntegrations: { kind: 'link', href: inClientAccount(ROUTES.INTEGRATIONS, entityUUID) },
    newTabCaption: captionFor(accountName),
    body: 'default'
  };
}
